import { assetUrl } from "./asset-paths.js";
import { createViewerWorker } from './viewer-worker.js';
import { fetchJson } from './http.js';

/** Asset worker lifetime for an embedded live map. Each load owns its RPCs and cancellation. */
export class LiveMapLoader {
    constructor({ renderer, cache, onProgress = () => {}, onDiagnostic = () => {}, onFailure = () => {},
        fetchMap = (...args) => fetch(...args), workerFactory = () => createViewerWorker('worker') }) {
        Object.assign(this, { renderer, cache, onProgress, onDiagnostic, onFailure, fetchMap, workerFactory });
        this.generation = 0;
        this.pending = new Map();
    }
    get assetsLoading() { return this.pending.size > 0; }
    cancel() {
        this.generation++;
        this.stop?.(); this.stop = null;
        this.cache.active?.clear();
        this.renderer.loading = false;
        this.renderer.clear(); this.sceneData = null;
    }
    async load(map, { signal } = {}) {
        this.cancel();
        const own = this.generation, abort = new AbortController(), pending = new Map();
        this.pending = pending;
        let worker, sequence = 0, settle, readyComplete = false;
        const cancelled = () => new DOMException('Cancelled', 'AbortError');
        const active = () => own === this.generation && !abort.signal.aborted;
        const stop = () => {
            abort.abort(); worker?.terminate();
            for (const request of pending.values()) request.reject(cancelled());
            pending.clear(); settle?.reject(cancelled());
            signal?.removeEventListener('abort', stop);
        };
        this.stop = stop;
        signal?.addEventListener('abort', stop, { once: true });
        if (signal?.aborted) stop();
        const progress = message => { if (active()) this.onProgress(message); };
        let details;
        const rpc = (type, data) => {
            if (!active()) return Promise.reject(cancelled());
            if (type === 'sound-script') return this.fetchMap(`/sound-script/${encodeURIComponent(data.entry)}?assetSet=${encodeURIComponent(details.assetSet)}&map=${encodeURIComponent(details.name)}`, { signal: abort.signal, cache: 'no-cache' })
                .then(async response => { if (!response.ok) throw new Error(`Sound script unavailable (${response.status})`); const script = await response.json(); if (!active()) throw cancelled(); return script; });
            return new Promise((resolve, reject) => {
                const requestId = ++sequence;
                pending.set(requestId, { resolve, reject });
                try { worker.postMessage({ type, loadId: own, requestId, ...data }); }
                catch (error) { pending.delete(requestId); reject(error); }
            });
        };
        this.renderer.loading = true;
        try {
            progress(`Loading ${map.name || 'map'}…`);
            details = await fetchJson(`/maps/${encodeURIComponent(map.name)}`, { signal: abort.signal, cache: 'no-cache' }, { fetcher: this.fetchMap });
            if (details.revision !== map.revision || details.assetSet !== map.assetSet)
                throw new Error('Map content changed. Waiting for the publisher to use the current content.');
            const bytes = await this.cache.fetch(details.bspUrl, abort.signal, (size, total) =>
                progress(`Downloading ${details.name} · ${(size / 1048576).toFixed(1)}${total ? ` / ${(total / 1048576).toFixed(1)}` : ''} MiB`));
            if (!active()) throw cancelled();
            worker = this.workerFactory();
            const ready = new Promise((resolve, reject) => { settle = { resolve, reject }; });
            void ready.catch(() => {}); // A synchronous postMessage failure can precede awaiting ready.
            const fail = error => {
                if (active()) this.onDiagnostic(error.message);
                settle.reject(error);
                for (const request of pending.values()) request.reject(error);
                pending.clear();
                if (readyComplete && active()) { stop(); this.onFailure(error); }
            };
            worker.onerror = () => fail(new Error('Map parser failed. Retry to load the map again.'));
            worker.onmessageerror = () => fail(new Error('Map parser response could not be read.'));
            worker.onmessage = async ({ data }) => {
                if (!active() || data.loadId !== own) return;
                if (data.type === 'asset' || data.type === 'particle-index') {
                    try {
                        const url = data.type === 'particle-index' ? `/particles?assetSet=${encodeURIComponent(details.assetSet)}&map=${encodeURIComponent(details.name)}`
                            : assetUrl(data.path, details.assetSet);
                        const asset = await this.cache.fetch(url, abort.signal);
                        if (active()) worker.postMessage({ type: 'asset', requestId: data.requestId, bytes: asset.buffer }, [asset.buffer]);
                    } catch (error) {
                        if (active()) worker.postMessage({ type: 'asset', requestId: data.requestId, error: error.message });
                    }
                } else if (data.type === 'progress') progress(data.stage);
                else if (data.type === 'diagnostic') this.onDiagnostic(data.message);
                else if (data.requestId) {
                    const request = pending.get(data.requestId); pending.delete(data.requestId);
                    if (data.type === 'sound' && !data.bytes) {
                        const path = data.path.split('/').map(encodeURIComponent).join('/');
                        const query = `?assetSet=${encodeURIComponent(details.assetSet)}`;
                        data.url = `/assets/${path}${query}`;
                        data.metadataUrl = `/sound-metadata/${path}${query}`;
                    }
                    if (data.type === 'error') request?.reject(new Error(data.message));
                    else request?.resolve(data);
                } else if (data.type === 'error') fail(new Error(data.message));
                else if (data.type === 'geometry') {
                    try {
                        await this.renderer.world(data, rpc, active, progress);
                        if (!active()) return;
                        await this.renderer.props(data.props, rpc, active, progress);
                        if (!active()) return;
                        this.sceneData = data; settle.resolve();
                    } catch (error) { fail(error); }
                }
            };
            worker.postMessage({ type: 'load', loadId: own, mapName: details.name, bytes: bytes.buffer }, [bytes.buffer]);
            await ready;
            if (!active()) throw cancelled();
            this.renderer.loading = false; readyComplete = true;
            return rpc;
        } catch (error) {
            stop();
            if (own === this.generation) { this.renderer.loading = false; this.renderer.clear(); }
            throw error;
        }
    }
    dispose() { this.cancel(); }
}
